import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { 
  FaCheckCircle, 
  FaUserGraduate, 
  FaChartBar, 
  FaApple,
  FaGooglePlay 
} from "react-icons/fa";
import "../styles/landing-page.css";
import "../styles/app-download.css";
import "../styles/custom-accordion.css"; // Import the custom accordion styles

const CoachingManagementSystem = () => {
  // Add state for managing active FAQ
  const [activeFaq, setActiveFaq] = useState("faq1");
  
  // Toggle function for FAQ items
  const toggleFaq = (id) => {
    setActiveFaq(activeFaq === id ? null : id);
  };

  // Structured data specific to coaching management software
  const coachingMgmtStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Pathshala Sanchalak Coaching Management Software",
    applicationCategory: "EducationalApplication, BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    description: "Complete coaching management software for institutes, tuition centers, and coaching classes to manage students, faculty, batches, and operations.",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.7",
      ratingCount: "128",
    },
    featureList: "Student Enrollment, Batch Management, Faculty Enrollment, Fee Management, Performance Tracking, Online Classes, Study Material Distribution",
  };

  return (
    <div className="landing-page">
      <Helmet>
        <title>Coaching Management Software - Complete Solution for Institutes | Pathshala Sanchalak</title>
        <meta 
          name="description" 
          content="Best coaching management system in India for coaching institutes, tuition centers & test prep academies. Manage batches, students & faculty easily. Try free today!" 
        />
        <meta
          name="keywords"
          content="coaching management system, coaching institute system, tuition center management, coaching class software, test preparation management system"
        />
        <script type="application/ld+json">
          {JSON.stringify(coachingMgmtStructuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section text-center py-5">
        <div className="container">
          <h1 className="display-4 fw-bold mb-4">Coaching Management Software</h1>
          <p className="lead mb-4">
            Comprehensive solution to streamline your coaching institute operations and maximize student success
          </p>
          <div className="cta-buttons">
            <Link to="/register" className="btn btn-primary btn-lg">
              Start Free Trial
            </Link>
            <Link to="/#contact" className="btn btn-primary btn-lg">
              Schedule Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="features-section py-5 bg-light">
        <div className="container">
          <h2 className="section-title text-center mb-5">Essential Features for Coaching Institutes</h2>
          <div className="row g-4">
            <div className="col-md-4">
              <div className="feature-card h-100 p-4">
                <div className="icon-wrapper mb-3">
                  <FaUserGraduate className="feature-icon" />
                </div>
                <h3 className="feature-title">Student & Batch Management</h3>
                <p>
                  Efficiently manage student enrollments, batch allocation, and track student progress across different courses.
                </p>
                <ul className="feature-list">
                  <li><FaCheckCircle className="icon-check" /> Student enrollment & profiles</li>
                  <li><FaCheckCircle className="icon-check" /> Batch creation and management</li>
                  <li><FaCheckCircle className="icon-check" /> AI Assistant for Students</li>
                  <li><FaCheckCircle className="icon-check" /> Study Material availability</li>
                  <li><FaCheckCircle className="icon-check" /> Performance tracking</li>

                </ul>
              </div>
            </div>
            {/* <div className="col-md-4">
              <div className="feature-card h-100 p-4">
                <div className="icon-wrapper mb-3">
                  <FaRupeeSign className="feature-icon" />
                </div>
                <h3 className="feature-title">Fee & Financial Management</h3>
                <p>
                  Streamline fee collection, manage installments, track payments, and generate financial reports instantly.
                </p>
                <ul className="feature-list">
                  <li><FaCheckCircle className="icon-check" /> Fee collection & receipts</li>
                  <li><FaCheckCircle className="icon-check" /> Payment reminders</li>
                  <li><FaCheckCircle className="icon-check" /> Financial reporting</li>
                </ul>
              </div>
            </div> */}
            <div className="col-md-4">
              <div className="feature-card h-100 p-4">
                <div className="icon-wrapper mb-3">
                  <FaChartBar className="feature-icon" />
                </div>
                <h3 className="feature-title">Performance Analytics</h3>
                <p>
                  Track student performance, analyze test results, and identify improvement areas with detailed analytics.
                </p>
                <ul className="feature-list">
                  <li><FaCheckCircle className="icon-check" /> Test & exam management</li>
                  <li><FaCheckCircle className="icon-check" /> Performance reports</li>
                  <li><FaCheckCircle className="icon-check" /> Student ranking system</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section py-5">
        <div className="container">
          <h2 className="section-title text-center mb-5">Why Choose Our Coaching Management Software?</h2>
          <div className="row g-4">
            <div className="col-md-6">
              <div className="benefit-item d-flex align-items-start mb-4">
                <div className="benefit-icon me-3">
                  <FaCheckCircle className="text-success" />
                </div>
                <div>
                  <h3 className="h5">Completely Free Solution</h3>
                  <p>All features available at no cost - no premium plans, no hidden charges.</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="benefit-item d-flex align-items-start mb-4">
                <div className="benefit-icon me-3">
                  <FaCheckCircle className="text-success" />
                </div>
                <div>
                  <h3 className="h5">User-Friendly Interface</h3>
                  <p>Intuitive design requiring minimal training for staff and faculty.</p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="benefit-item d-flex align-items-start mb-4">
                <div className="benefit-icon me-3">
                  <FaCheckCircle className="text-success" />
                </div>
                <div>
                  <h3 className="h5">Online Class Integration</h3>
                  <p>Seamlessly conduct and manage online coaching sessions within the platform.</p>
                </div>
              </div>
            </div>
            {/* <div className="col-md-6">
              <div className="benefit-item d-flex align-items-start mb-4">
                <div className="benefit-icon me-3">
                  <FaCheckCircle className="text-success" />
                </div>
                <div>
                  <h3 className="h5">Multi-Branch Support</h3>
                  <p>Manage multiple coaching centers from a single dashboard.</p>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </section>

      {/* App Download Section */}
      <section className="app-download-section py-5">
        <div className="container">
          <div className="app-download-container">
            <div className="app-info">
              <h2>Mobile Apps for Your Coaching Institute</h2>
              <p>Take your coaching management to the next level with our dedicated mobile applications for students and faculty members.</p>
              
              <div className="app-category mt-4 mb-3">
                <h4>For Students: Chhaatr App</h4>
                <p>Students can access study materials, test schedules, performance reports, and communicate with teachers anytime, anywhere.</p>
                <div className="app-store-links">
                  <a 
                    href="https://apps.apple.com/in/app/ps-chhaatr/id6745966365" 
                    className="app-store-btn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Download Chhaatr App on App Store"
                  >
                    <FaApple /> App Store
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.bojack.chhaatrpathshalasanchalakapp&pcampaignid=web_share" 
                    className="play-store-btn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Download Chhaatr App on Google Play"
                  >
                    <FaGooglePlay /> Google Play
                  </a>
                </div>
              </div>
              
              <div className="app-category mt-4">
                <h4>For Faculty: Adhyapak App</h4>
                <p>Faculty can manage batches, update study materials, conduct tests, and track student performance from their mobile devices.</p>
                <div className="app-store-links">
                  <a 
                    href="https://apps.apple.com/in/app/ps-adhyapak/id6745964284" 
                    className="app-store-btn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Download Adhyapak App on App Store"
                  >
                    <FaApple /> App Store
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.bojack.adhyapakpathshalasanchalakapp&pcampaignid=web_share" 
                    className="play-store-btn" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Download Adhyapak App on Google Play"
                  >
                    <FaGooglePlay /> Google Play
                  </a>
                </div>
              </div>
            </div>
            
            <div className="app-screenshots d-none d-md-flex">
              {/* <img 
                src="/images/apps/chhaatr-app-ss.png" 
                alt="Chhaatr App Screenshot" 
                className="app-screenshot"
              />
              <img 
                src="/images/apps/adhyapak-app-ss copy.png"  
                alt="Adhyapak App Screenshot" 
                className="app-screenshot second"
              /> */}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5 bg-primary text-white text-center">
        <div className="container">
          <h2 className="mb-4">Ready to transform your coaching institute?</h2>
          <p className="lead mb-4">Start your journey with our all-in-one software built for coaching centers.</p>
            <Link to="/register" className="btn btn-primary btn-lg">
              Get Started for Free
            </Link>
        </div>
      </section>

      {/* FAQ Section for SEO - Updated with custom accordion */}
      <section className="faq-section py-5 bg-light">
        <div className="container">
          <h2 className="section-title text-center mb-5">Frequently Asked Questions</h2>
          <div className="custom-accordion">
            <div className="custom-accordion-item mb-3">
              <h3 
                className={`custom-accordion-header ${activeFaq === "faq1" ? "active" : ""}`}
                onClick={() => toggleFaq("faq1")}
              >
                What is coaching management software?
              </h3>
              <div className={`custom-accordion-body ${activeFaq === "faq1" ? "show" : ""}`}>
                Coaching management software is a specialized solution designed for coaching institutes, tuition centers, and test preparation academies to manage their operations efficiently. It helps in managing student admissions, batch scheduling, faculty allocation, fee collection, attendance tracking, and performance analysis in one integrated platform.
              </div>
            </div>
            
            <div className="custom-accordion-item mb-3">
              <h3 
                className={`custom-accordion-header ${activeFaq === "faq2" ? "active" : ""}`}
                onClick={() => toggleFaq("faq2")}
              >
                How can coaching management software help my institute?
              </h3>
              <div className={`custom-accordion-body ${activeFaq === "faq2" ? "show" : ""}`}>
                Our coaching management software helps institutes by automating administrative tasks, streamlining student enrollment, simplifying batch management, tracking attendance digitally, managing fee collection efficiently, analyzing student performance, conducting online classes, and providing comprehensive reports for data-driven decisions. This allows coaching institute owners and staff to focus more on teaching quality and student outcomes rather than administrative burdens.
              </div>
            </div>
            
            <div className="custom-accordion-item mb-3">
              <h3 
                className={`custom-accordion-header ${activeFaq === "faq3" ? "active" : ""}`}
                onClick={() => toggleFaq("faq3")}
              >
                Is Pathshala Sanchalak coaching management software suitable for small tuition centers?
              </h3>
              <div className={`custom-accordion-body ${activeFaq === "faq3" ? "show" : ""}`}>
                Yes, Pathshala Sanchalak is designed to scale with your needs. It works perfectly for small tuition centers with just a few students, as well as large coaching institutes with multiple branches. The interface is user-friendly and requires minimal setup, making it ideal for coaching centers of all sizes.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CoachingManagementSystem;
